import { useState, useEffect, useCallback } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';

/**
 * useAjouterFacture - Hook pour gérer le formulaire d'ajout de facture
 *
 * @param {boolean} isOpen - État d'ouverture de la modale
 * @param {Function} onSubmit - Callback de soumission
 * @param {string} type - 'externe' ou 'interne'
 */
export function useAjouterFacture(isOpen, onSubmit, type) {
  const getInitialFormData = useCallback(() => ({
    // Champs communs
    numeroFacture: '',
    tauxTVA: '20',
    intitule: '',
    // Spécifique aux factures externes
    prestataire: '',
    mois: '',
    anneeExterne: new Date().getFullYear().toString(),
    // Spécifique aux factures internes
    consultant: '',
    trimestre: '',
    anneeInterne: new Date().getFullYear().toString(),
    destinataireType: 'ephyco',
    destinataire: '',
  }), []);

  const [formData, setFormData] = useState(getInitialFormData);
  const [loadingNumero, setLoadingNumero] = useState(false);
  const [lines, setLines] = useState([{ description: '', montant: '' }]);

  // Compteur incrémenté après une reconnexion pour forcer le recalcul du numéro
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const handler = () => setRefreshKey(k => k + 1);
    window.addEventListener('session:renewed', handler);
    return () => window.removeEventListener('session:renewed', handler);
  }, []);

  // Réinitialiser à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setFormData(getInitialFormData());
      setLoadingNumero(false);
      setLines([{ description: '', montant: '' }]);
    }
  }, [isOpen, getInitialFormData]);

  // Auto-calcul du numéro quand le prestataire change (externe)
  useEffect(() => {
    if (type !== 'externe') return;

    if (!formData.prestataire) {
      setFormData(prev => ({ ...prev, numeroFacture: '' }));
      return;
    }

    let cancelled = false;
    setLoadingNumero(true);

    (async () => {
      try {
        const r = await fetchWithAuth('/.netlify/functions/getNextFactureExterne');
        const data = await r.json();
        if (!cancelled) setFormData(prev => ({ ...prev, numeroFacture: data.numero || '' }));
      } catch {
        // 401 → session expirée géré par fetchWithAuth
      } finally {
        if (!cancelled) setLoadingNumero(false);
      }
    })();

    return () => { cancelled = true; };
  }, [formData.prestataire, type, refreshKey]);

  // Auto-calcul du numéro quand le consultant change (interne)
  useEffect(() => {
    if (type !== 'interne') return;

    if (!formData.consultant) {
      setFormData(prev => ({ ...prev, numeroFacture: '' }));
      return;
    }

    let cancelled = false;
    setLoadingNumero(true);

    (async () => {
      try {
        const r = await fetchWithAuth(`/.netlify/functions/getNextFactureInterne?consultantId=${formData.consultant}`);
        const data = await r.json();
        if (!cancelled) setFormData(prev => ({ ...prev, numeroFacture: data.numero || '' }));
      } catch {
        // 401 → session expirée géré par fetchWithAuth
      } finally {
        if (!cancelled) setLoadingNumero(false);
      }
    })();

    return () => { cancelled = true; };
  }, [formData.consultant, type, refreshKey]);

  // Handler de changement
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Handlers de lignes
  const handleAddLine = useCallback(() => {
    setLines(prev => [...prev, { description: '', montant: '' }]);
  }, []);

  const handleRemoveLine = useCallback((index) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleLineChange = useCallback((index, field, value) => {
    setLines(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  // Calculs des montants (depuis les lignes)
  const montantHT = lines.reduce((sum, l) => sum + (parseFloat(l.montant) || 0), 0);
  const tauxTVA = parseFloat(formData.tauxTVA) || 0;
  const montantTVA = montantHT * (tauxTVA / 100);
  const montantTTC = montantHT + montantTVA;

  // Handler de soumission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const prestataireId = formData.destinataireType === 'prestataire' ? formData.destinataire : null;
    onSubmit({ ...formData, lines, montantHT, tauxTVA, montantTVA, montantTTC, prestataireId });
  }, [formData, lines, montantHT, tauxTVA, montantTVA, montantTTC, onSubmit]);

  return {
    formData,
    handleChange,
    handleSubmit,
    loadingNumero,
    lines,
    handleAddLine,
    handleRemoveLine,
    handleLineChange,
    montantHT,
    tauxTVA,
    montantTVA,
    montantTTC
  };
}
